import { useEffect, useState } from 'react';
import './competitive-monitoring.css';
import { useAuth } from '../../shared/auth/AuthContext.jsx';
import { ClientProvider, useClient } from './ClientContext.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import ImportDataPage from './pages/ImportDataPage.jsx';
import RecalibrationPage from './pages/RecalibrationPage.jsx';
import DataMappingPage from './pages/DataMappingPage.jsx';

import OrbitModuleHeader from '../../shared/hub/OrbitModuleHeader.jsx';
import { T as O } from '../../shared/styles/tokens.js';

// Key names kept so the module's remaining call sites read unchanged.
const T = {
  bg:      O.paper,
  surface: O.surface,
  border:  O.rule,
  teal:    O.ink,
  text:    O.text,
  muted:   O.text3,
};

// Ordered by how often they are opened, not by the order data flows through them: the
// dashboard is looked at constantly, imports once a month. Dashboard first also makes the
// non-admin landing tab the first visible one rather than a special case.
//
// Dashboard is the one client-facing view and is not admin-gated. It absorbed Calibrated
// Views: two tabs over the same numbers meant the calibrated figure and the explanation of
// it lived apart, which is the opposite of what a client conversation needs.
const NAV = [
  { id: 'dashboard', label: 'Dashboard',     adminOnly: false, path: '' },
  { id: 'actual',    label: 'Recalibration', adminOnly: true, path: '/recalibration' },
  { id: 'mapping',   label: 'Data Mapping',  adminOnly: true, path: '/data-mapping' },
  { id: 'import',    label: 'Data Import',   adminOnly: true, path: '/data-import' },
];

function readTab() {
  const path = window.location.pathname.slice('/competitive-monitoring'.length).replace(/\/$/, '');
  return NAV.find(tab => tab.path === path)?.id || 'dashboard';
}

export default function CompetitiveMonitoringApp() {
  return (
    <ClientProvider>
      <CompetitiveMonitoringShell />
    </ClientProvider>
  );
}

function CompetitiveMonitoringShell() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState(readTab);

  useEffect(() => {
    const onPop = () => setTab(readTab());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navigateTab(id) {
    history.pushState(null, '', `/competitive-monitoring${NAV.find(t => t.id === id)?.path || ''}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  const visibleNav = NAV.filter(n => !n.adminOnly || isAdmin);
  // Fall back to the first visible tab rather than hard-pinning non-admins to the
  // dashboard: there is now more than one client-facing view, and pinning would make the
  // others unreachable while still rendering their nav links.
  const activeTab = visibleNav.some(n => n.id === tab) ? tab : visibleNav[0]?.id;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text }}>
      {/* No Back to Hub here — this module is opened in its own tab. */}
      <OrbitModuleHeader
        title="Competitive Monitoring"
        tabs={visibleNav}
        activeTab={activeTab}
        onTabChange={navigateTab}
        moduleId="competitive-monitoring"
        showHub={false}
      />

      <main className="o-page cm-page">
        {activeTab === 'import' ? <ImportDataPage />
          : activeTab === 'mapping' ? <DataMappingPage />
          : activeTab === 'actual' ? <RecalibrationPage />
          : <DashboardPage />}
      </main>
    </div>
  );
}
